import React, { useState, useEffect, useRef } from 'react';
import { FaBitcoin, FaEthereum } from 'react-icons/fa';
import { SiRipple, SiLitecoin, SiDogecoin, SiTether } from 'react-icons/si';
import { IoTrendingUp, IoTrendingDown } from 'react-icons/io5';

const LiveTransactionsBot = () => {
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [volumeToday, setVolumeToday] = useState(0);
  const transactionQueueRef = useRef([]);
  const animationTimeoutRef = useRef(null);

  // Style object for inline styling
  const styles = {
    container: {
      backgroundColor: '#1E293B',
      borderRadius: '12px',
      margin: '16px',
      overflow: 'hidden',
      border: '1px solid #334155',
      position: 'relative',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #334155',
    },
    title: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#F8FAFC',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    stats: {
      display: 'flex',
      gap: '16px',
      fontSize: '12px',
      color: '#94A3B8',
    },
    statValue: {
      color: '#38BDF8',
      fontWeight: '500',
    },
    liveIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    liveDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#10B981',
      animation: 'pulse 1.5s infinite',
    },
    liveText: {
      color: '#10B981',
      fontWeight: '500',
      fontSize: '12px',
    },
    transactionContainer: {
      padding: '20px 20px',
      minHeight: '120px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    transaction: {
      width: 'calc(100% - 32px)',
      padding: '16px',
      borderRadius: '8px',
      backgroundColor: 'rgba(15, 23, 42, 0.6)',
      border: '1px solid #334155',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'absolute',
      top: '20px',
      left: '16px',
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'opacity 0.4s ease, transform 0.4s ease',
    },
    transactionActive: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    transactionExit: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    cryptoInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    cryptoIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
    },
    btcIcon: {
      backgroundColor: 'rgba(247, 147, 26, 0.2)',
      color: '#F7931A',
    },
    ethIcon: {
      backgroundColor: 'rgba(98, 126, 234, 0.2)',
      color: '#627EEA',
    },
    xrpIcon: {
      backgroundColor: 'rgba(0, 102, 177, 0.2)',
      color: '#0066B1',
    },
    ltcIcon: {
      backgroundColor: 'rgba(181, 181, 181, 0.2)',
      color: '#B5B5B5',
    },
    dogeIcon: {
      backgroundColor: 'rgba(195, 166, 52, 0.2)',
      color: '#C3A634',
    },
    usdtIcon: {
      backgroundColor: 'rgba(38, 161, 123, 0.2)',
      color: '#26A17B',
    },
    cryptoName: {
      display: 'flex',
      flexDirection: 'column',
    },
    cryptoSymbol: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#F8FAFC',
    },
    phone: {
      fontSize: '12px',
      color: '#94A3B8',
    },
    amountContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '4px',
    },
    amount: {
      fontSize: '16px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    depositAmount: {
      color: '#10B981',
    },
    withdrawAmount: {
      color: '#EF4444',
    },
    timestamp: {
      fontSize: '12px',
      color: '#94A3B8',
    },
    typeTag: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase',
    },
    depositTag: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
    },
    withdrawTag: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
    },
    progressBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: '2px',
      backgroundColor: '#38BDF8',
      width: '0%',
      transition: 'width linear',
    }
  };

  // Generate random phone number
  const generateRandomMobileNumber = () => {
    const prefixes = ['01', '07', '2547'];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const middleDigits = Math.floor(Math.random() * 9000) + 1000;
    const lastDigits = Math.floor(Math.random() * 9000) + 1000;
    return `${prefix}${middleDigits}${lastDigits}`;
  };

  // Generate random amount
  const generateRandomAmount = () => {
    return Math.floor(Math.random() * (200000 - 100 + 1)) + 100;
  };

  // Generate random crypto
  const getRandomCrypto = () => {
    const cryptos = ['BTC', 'ETH', 'XRP', 'LTC', 'DOGE', 'USDT'];
    return cryptos[Math.floor(Math.random() * cryptos.length)];
  };

  // Map crypto symbol to full name
  const getCryptoFullName = (symbol) => {
    const map = {
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'XRP': 'Ripple',
      'LTC': 'Litecoin',
      'DOGE': 'Dogecoin',
      'USDT': 'Tether'
    };
    return map[symbol] || symbol;
  };

  // Get crypto icon based on symbol
  const getCryptoIconStyles = (crypto) => {
    switch(crypto) {
      case 'BTC':
        return { icon: <FaBitcoin />, style: styles.btcIcon };
      case 'ETH':
        return { icon: <FaEthereum />, style: styles.ethIcon };
      case 'XRP':
        return { icon: <SiRipple />, style: styles.xrpIcon };
      case 'LTC':
        return { icon: <SiLitecoin />, style: styles.ltcIcon };
      case 'DOGE':
        return { icon: <SiDogecoin />, style: styles.dogeIcon };
      case 'USDT':
        return { icon: <SiTether />, style: styles.usdtIcon };
      default:
        return { icon: <FaBitcoin />, style: styles.btcIcon };
    }
  };

  // Generate a new transaction
  const generateNewTransaction = () => {
    const type = Math.random() > 0.5 ? 'deposit' : 'withdraw';
    const crypto = getRandomCrypto();
    
    return {
      id: Date.now() + Math.random(),
      type: type,
      crypto: crypto,
      cryptoName: getCryptoFullName(crypto),
      phone: generateRandomMobileNumber(),
      amount: generateRandomAmount(),
      timestamp: 'Just now'
    };
  };

  // Process the next transaction in queue
  const processNextTransaction = () => {
    if (transactionQueueRef.current.length === 0) {
      // Generate new transactions if queue is empty
      for (let i = 0; i < 3; i++) {
        transactionQueueRef.current.push(generateNewTransaction());
      }
    }
    
    // Start transition out if there's a current transaction
    if (currentTransaction) {
      setIsTransitioning(true);
      
      // Wait for exit animation to complete
      setTimeout(() => {
        // Get next transaction from queue
        const nextTransaction = transactionQueueRef.current.shift();
        setCurrentTransaction(nextTransaction);
        setTotalTransactions(prev => prev + 1);
        setVolumeToday(prev => prev + nextTransaction.amount);
        setIsTransitioning(false);
        
        // Start progress bar animation
        const progressBar = document.getElementById('progress-bar');
        if (progressBar) {
          progressBar.style.width = '0%';
          progressBar.style.transition = 'width 4s linear';
          setTimeout(() => {
            progressBar.style.width = '100%';
          }, 50);
        }
        
        // Schedule next transaction
        animationTimeoutRef.current = setTimeout(processNextTransaction, 4000);
      }, 400);
    } else {
      // Initial transaction
      const nextTransaction = transactionQueueRef.current.shift();
      setCurrentTransaction(nextTransaction);
      setTotalTransactions(prev => prev + 1);
      setVolumeToday(prev => prev + nextTransaction.amount);
      
      // Start progress bar animation
      setTimeout(() => {
        const progressBar = document.getElementById('progress-bar');
        if (progressBar) {
          progressBar.style.width = '0%';
          progressBar.style.transition = 'width 4s linear';
          setTimeout(() => {
            progressBar.style.width = '100%';
          }, 50);
        }
      }, 50);
      
      // Schedule next transaction
      animationTimeoutRef.current = setTimeout(processNextTransaction, 4000);
    }
  };

  useEffect(() => {
    // Initialize transaction queue and display first transaction
    for (let i = 0; i < 5; i++) {
      transactionQueueRef.current.push(generateNewTransaction());
    }
    
    // Initialize total volume
    setVolumeToday(Math.floor(Math.random() * 5000000) + 1000000);
    
    // Start processing transactions
    processNextTransaction();
    
    // Clean up on unmount
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // If no current transaction, show a loading state
  if (!currentTransaction) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>Live Transactions</div>
        </div>
        <div style={styles.transactionContainer}>
          <div>Loading transactions...</div>
        </div>
      </div>
    );
  }

  // Get icon and styling for current transaction's crypto
  const { icon, style: iconStyle } = getCryptoIconStyles(currentTransaction.crypto);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>
          Transactions
          <div style={styles.liveIndicator}>
            <div style={styles.liveDot}></div>
            <span style={styles.liveText}>LIVE</span>
          </div>
        </div>
        <div style={styles.stats}>
          <span>
            Volume: <span style={styles.statValue}>KES {(volumeToday / 1000000).toFixed(2)}M</span>
          </span>
        </div>
      </div>
      
      <div style={styles.transactionContainer}>
        <div 
          style={{
            ...styles.transaction,
            ...(isTransitioning ? styles.transactionExit : styles.transactionActive)
          }}
        >
          <div style={styles.details}>
            <div style={styles.cryptoInfo}>
              <div style={{...styles.cryptoIcon, ...iconStyle}}>
                {icon}
              </div>
              <div style={styles.cryptoName}>
                <span style={styles.cryptoSymbol}>{currentTransaction.crypto}</span>
                <span style={styles.phone}>
                  {currentTransaction.phone.slice(0,2)}****{currentTransaction.phone.slice(-3)}
                </span>
              </div>
            </div>
            <div 
              style={{
                ...styles.typeTag,
                ...(currentTransaction.type === 'deposit' ? styles.depositTag : styles.withdrawTag)
              }}
            >
              {currentTransaction.type}
            </div>
          </div>
          
          <div style={styles.amountContainer}>
            <div 
              style={{
                ...styles.amount,
                ...(currentTransaction.type === 'deposit' ? styles.depositAmount : styles.withdrawAmount)
              }}
            >
              {currentTransaction.type === 'deposit' ? <IoTrendingUp /> : <IoTrendingDown />}
              KES {currentTransaction.amount.toLocaleString()}
            </div>
            <div style={styles.timestamp}>{currentTransaction.timestamp}</div>
          </div>
        </div>
      </div>
      
      <div id="progress-bar" style={styles.progressBar}></div>
      
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default LiveTransactionsBot;